import * as React from 'react';
import { gql } from '@apollo/client';
import {
  SelectCarFragment,
  useListCarsForCustomerQuery,
} from './__generated__/SelectCar.generated';
import { ScrollArea } from 'src/shadcn/components/ui/scroll-area';
import { notEmpty } from 'src/utils/utils';
import { Button } from 'src/shadcn/components/ui/button';
import { Separator } from 'src/shadcn/components/ui/separator';

export interface ISelectCarProps {
  onSelectCar: (car: SelectCarFragment) => void;
  customerId: string;
  carIdToHide?: string;
}

export const SelectCar: React.FunctionComponent<ISelectCarProps> = props => {
  const { isLoadingCars, listCarsResponse, fetchMoreCars } = useListCars(
    props.customerId
  );
  return (
    <>
      {isLoadingCars && <div>Loading...</div>}
      {!isLoadingCars && listCarsResponse?.items.length === 0 && (
        <div>No cars found</div>
      )}
      <ScrollArea className='max-h-[70vh] w-full'>
        {listCarsResponse?.items.map((car, i) => {
          return (
            <div key={car.id}>
              <div className='flex items-center justify-between rounded p-5'>
                <div>
                  <div>
                    {[car.colour, car.year, car.make, car.model, car.trim]
                      .filter(notEmpty)
                      .join(' ')}
                  </div>
                  {car.vin && (
                    <div className='text-muted-foreground'>Vin: {car.vin}</div>
                  )}

                  {car.licensePlate && (
                    <div className='text-muted-foreground'>
                      License plate: {car.licensePlate}
                    </div>
                  )}
                </div>
                <Button
                  id={`select-car-button-${car.id}`}
                  variant={'ghost'}
                  onClick={() => {
                    props.onSelectCar(car);
                  }}
                >
                  <span>Select</span>
                </Button>
              </div>
              {i !== listCarsResponse.items.length - 1 ? <Separator /> : null}
            </div>
          );
        })}
      </ScrollArea>
    </>
  );
};

function useListCars(customerId: string) {
  const {
    loading: isLoadingCars,
    data: listCarsResponse,
    fetchMore: fetchMoreCars,
  } = useListCarsForCustomerQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: { customerId, offset: undefined },
    onError: () => {
      // TODO: handle
    },
    skip: !customerId,
  });

  return {
    isLoadingCars,
    listCarsResponse: listCarsResponse?.listCars,
    fetchMoreCars: listCarsResponse?.listCars.offset
      ? () =>
          fetchMoreCars({
            variables: {
              customerId,
              offset: listCarsResponse?.listCars.offset,
            },
          })
      : undefined,
  };
}

const fragment = gql`
  fragment SelectCar on Car {
    id
    year
    make
    model
    colour
    vin
    licensePlate
  }
`;

const LIST_CARS_FOR_CUSTOMER = gql`
  query ListCarsForCustomer($customerId: String!, $offset: Int) {
    listCars(customerId: $customerId, offset: $offset) {
      items {
        make
        model
        year
        colour
        id
        createdAt
        vin
        licensePlate
        trim
      }
      offset
    }
  }
`;
